import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import Sidebar from '../../components/patient/PatientSidebar';
import Logo from '../../components/common/Logo';
import AppointmentService from '../../services/appoinment.service';
import PaymentService from '../../services/payment.service';
import { FileDown } from 'lucide-react';
import NavigationButtons from '../../components/common/NavigationButtons';

const PatientBookingSummaryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { doctor, appointmentData } = location.state || {};
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [consentoInformato, setConsentoInformato] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState(false);
  const [hasExistingAppointments, setHasExistingAppointments] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [checkingDiscount, setCheckingDiscount] = useState(false);

  // Funzione per determinare quale consenso informato mostrare
  const getConsensoInformatoFile = () => {
    if (appointmentData && appointmentData.service_name) {
      if (appointmentData.service_name.toLowerCase().includes('logopedia')) {
        return {
          path: "/assets/pdf/Consenso-informato-Personal-Therapy-LOGOPEDIA.pdf",
          name: "Consenso informato per Logopedia"
        };
      }
        else if (appointmentData.service_name.toLowerCase().includes('psicoterapia di coppia')) {
            return {
                path: "/assets/pdf/Consenso-informato-Personal-Therapy-COPPIE.pdf",
                name: "Consenso informato per terapia di coppia"
            };
        }
        else if (appointmentData.service_name.toLowerCase().includes('familiare')) {
            return {
                path: "/assets/pdf/Consenso-informato-Personal-Therapy-FAMIGLIE.pdf",
                name: "Consenso informato per terapia familiare"
            };
        }
        else if (appointmentData.service_name.toLowerCase().includes('genitoriale')) {
            return {
                path: "/assets/pdf/Consenso-informato-Personal-Therapy-GENITORI.pdf",
                name: "Consenso informato per terapia genitoriale"
            };
        }
        else if (appointmentData.service_name.toLowerCase().includes('individuale')) {
            return {
                path: "/assets/pdf/Consenso-informato-Personal-Therapy-INDIVIDUALE.pdf",
                name: "Consenso informato per terapia individuale"
            };
        }
    }

    // Consenso informato di default
    return {
      path: "/assets/pdf/Consenso-informato-Personal-Therapy.pdf",
      name: "Consenso informato"
    };
  };

  // Ottieni il file di consenso appropriato
  const consensoFile = getConsensoInformatoFile();

  // Reset isProcessing quando la pagina viene caricata o l'utente torna indietro
  useEffect(() => {
    // Se l'utente torna indietro da Stripe, resetta lo stato di processing
    setIsProcessing(false);
  }, []);

  // Controllo se l'utente ha già effettuato appuntamenti - utilizzando lo stesso approccio di PatientBookingHistoryPage
  useEffect(() => {
    const checkPreviousAppointments = async () => {
      try {
        setIsLoading(true);

        const token = localStorage.getItem('auth_token');
        console.log('Token prima della richiesta:', token);

        console.log('Sto per chiamare getPatientAppointments()');
        const response = await AppointmentService.getPatientAppointments();

        // Assicurati che i dati siano un array
        const appointmentsArray = Array.isArray(response) ? response : (Array.isArray(response.data) ? response.data : []);
        console.log('Array finale degli appuntamenti:', appointmentsArray);

        // Modifica qui: filtra solo gli appuntamenti con status "confirmed"
        const confirmedAppointments = appointmentsArray.filter(appointment =>
          appointment.status === 'confirmed'
        );

        // Se ha appuntamenti confermati, imposta hasExistingAppointments a true
        setHasExistingAppointments(confirmedAppointments && confirmedAppointments.length > 0);

        // Se ha già appuntamenti confermati, imposta automaticamente il consenso come accettato
        if (confirmedAppointments && confirmedAppointments.length > 0) {
          setConsentoInformato(true);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Errore nel caricamento degli appuntamenti:', err);
        setIsLoading(false);
      }
    };

    checkPreviousAppointments();
  }, []);

  // Verifica eligibilità sconto fedeltà
  useEffect(() => {
    const checkDiscount = async () => {
      if (!doctor || !appointmentData) return;
      
      try {
        setCheckingDiscount(true);
        const discount = await PaymentService.checkDiscount(
          doctor.therapist_id,
          parseFloat(appointmentData.price)
        );
        setDiscountInfo(discount);
        console.log('Discount info:', discount);
      } catch (err) {
        console.error('Errore nel controllo dello sconto:', err);
        // In caso di errore, non blocchiamo il flusso
        setDiscountInfo(null);
      } finally {
        setCheckingDiscount(false);
      }
    };

    checkDiscount();
  }, [doctor, appointmentData]);

  // Verifica coupon
  const validateCoupon = () => {
    // Coupon permanente di test
    if (couponCode === 'test2025') {
      setCouponSuccess(true);
      setCouponError('');
      return;
    }

    // Coupon valido per 7 giorni
    const today = new Date();
    const expiryDate = new Date(today);
    expiryDate.setDate(today.getDate() + 7);

    if (couponCode === 'week2026' && new Date() <= expiryDate) {
      setCouponSuccess(true);
      setCouponError('');
      return;
    }

    setCouponSuccess(false);
    setCouponError('Codice coupon non valido');
  };



  const handleConfirmBooking = async () => {
    if (!hasExistingAppointments && !consentoInformato) {
      alert("Per procedere devi accettare il consenso informato");
      return;
    }

    setIsProcessing(true);

    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      console.log('Current User:', currentUser);

      let appointmentId;

      // CASO 1: Appuntamento suggested esistente
      if (appointmentData.existingAppointmentId && appointmentData.isFromSuggested) {
        console.log('Usando appuntamento suggested esistente:', appointmentData.existingAppointmentId);

        appointmentId = appointmentData.existingAppointmentId;

        // Se c'è il coupon, aggiorna lo status a 'confirmed' e salta il pagamento
        if (couponSuccess) {
          await AppointmentService.updateStatus(appointmentId, 'confirmed');
          navigate('/payment/success?payment_status=success');
          return;
        }

        // Se NON c'è coupon, NON toccare lo status
        // Lascialo 'suggested' - sarà il webhook di Stripe a cambiarlo dopo il pagamento
        console.log('Appuntamento rimane suggested fino al completamento del pagamento');
      }
      // CASO 2: Nuovo appuntamento
      else {
        const appointmentDataToSend = {
          ...appointmentData,
          therapist_id: doctor.id,
          status: couponSuccess ? 'confirmed' : 'booked'
        };

        const appointmentResponse = await AppointmentService.create(appointmentDataToSend);
        appointmentId = appointmentResponse.id;

        console.log('Nuovo appuntamento creato con ID:', appointmentId);

        // Se c'è il coupon, salta il pagamento
        if (couponSuccess) {
          navigate('/payment/success?payment_status=success');
          return;
        }
      }

      // Procedi con il pagamento Stripe
      // Usa il prezzo scontato se disponibile, altrimenti il prezzo normale
      const priceInEuros = discountInfo && discountInfo.eligible 
        ? discountInfo.final_price 
        : parseFloat(appointmentData.price);
      
      // Salva l'informazione se era un appuntamento suggested prima del pagamento
      if (appointmentData.existingAppointmentId && appointmentData.isFromSuggested) {
        sessionStorage.setItem('pendingPayment', JSON.stringify({
          appointmentId: appointmentId,
          wasSuggested: true,
          originalStatus: 'suggested'
        }));
      }
      
      const checkoutResponse = await PaymentService.createCheckoutSession(
        appointmentId,
        priceInEuros
      );

      console.log('Checkout session created per appointment:', appointmentId);
      window.location.href = checkoutResponse.sessionUrl;

    } catch (error) {
      console.error('Error processing appointment:', error);
      alert(error.message || 'Errore nella gestione dell\'appuntamento');
      setIsProcessing(false);
    }
  };


  // Controlla se l'utente sta tornando da un pagamento completato
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status');

    if (paymentStatus === 'success') {
      setBookingSuccess(true);
    }
  }, []);

  if (!doctor || !appointmentData) {
    return <Navigate to="/patient/doctors" />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <div className="w-64 bg-white shadow-md">
          <div className="p-4">
            <Logo />
          </div>
          <Sidebar />
        </div>
        <div className="flex-1 p-8 bg-[#FAF4EB] flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
            <p className="mt-2 text-gray-600">Caricamento in corso...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <Sidebar />
      </div>

      <div className="flex-1 p-8 bg-[#FAF4EB]">
        <h1 className="text-2xl font-bold mb-6">Riepilogo Prenotazione</h1>

        <NavigationButtons listPath="/patient/dashboard" />


        {bookingSuccess ? (
          <div className="bg-white p-6 rounded-md shadow-md text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-green-600 mb-2">Pagamento Effettuato con Successo</h2>
              <p className="text-gray-600 mb-6">Il tuo appuntamento è stato confermato! Riceverai le istruzioni via mail!</p>
            </div>
            <button
              onClick={() => navigate('/patient/appointments')}
              className="w-full bg-purple-600 text-white py-2 px-4 rounded hover:bg-purple-700"
            >
              Visualizza i tuoi prossimi appuntamenti
            </button>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-md shadow-md">
            <div className="mb-4">
              <h3 className="font-semibold">Professionista</h3>
              <p>{doctor.name}</p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold">Servizio</h3>
              <p>{appointmentData.service_name}</p>
            </div>
            <div className="mb-4">
              <h3 className="font-semibold">Data e Ora</h3>
              <p>
                {format(new Date(appointmentData.date), 'dd/MM/yyyy', { locale: it })} - {appointmentData.time}
              </p>
            </div>
            <div className="mb-6">
              <h3 className="font-semibold">Prezzo</h3>
              {discountInfo && discountInfo.eligible ? (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-lg text-gray-500 line-through">€{appointmentData.price}</p>
                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded">
                      -{discountInfo.discount_percentage}% Sconto Fedeltà
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">€{discountInfo.final_price}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Hai completato {discountInfo.completed_sessions} sedute con questo terapeuta!
                  </p>
                </div>
              ) : (
                <p className="text-xl font-bold">€{appointmentData.price}</p>
              )}
            </div>

            {/* Coupon */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-semibold mb-2">COUPON</h3>
              <div className="flex">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Inserisci il codice coupon"
                  className="flex-1 p-2 border border-gray-300 rounded-l"
                />
                <button
                  onClick={validateCoupon}
                  className="bg-purple-600 text-white px-4 py-2 rounded-r hover:bg-purple-700"
                >
                  Applica
                </button>
              </div>
              {couponError && <p className="text-red-500 text-sm mt-1">{couponError}</p>}
              {couponSuccess && (
                <div className="mt-2 text-green-600 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Coupon applicato! Il pagamento verrà saltato.
                </div>
              )}
            </div>

            {/* Consenso informato - mostrato solo se è il primo appuntamento */}
            {!hasExistingAppointments && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="consenso"
                    checked={consentoInformato}
                    onChange={e => setConsentoInformato(e.target.checked)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <label htmlFor="consenso" className="text-gray-700 cursor-pointer">
                      Ho preso visione e ho accettato il {consensoFile.name}
                    </label>
                    <div className="mt-2">
                      <a
                        href={consensoFile.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-purple-600 hover:text-purple-800"
                      >
                        <FileDown className="w-4 h-4 mr-1" />
                        Scarica il documento
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Se l'utente ha già appuntamenti, mostriamo un messaggio informativo */}
            {hasExistingAppointments && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center text-green-700">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Hai già accettato il consenso informato in precedenza.</span>
                </div>
              </div>
            )}

            {!couponSuccess && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2">Informazioni sul pagamento</h4>
                <p className="text-sm text-blue-700 mb-2">
                  Procederai al pagamento sicuro tramite Stripe. Accettiamo tutte le principali carte di credito.
                </p>
                <div className="flex space-x-3 mt-2">
                  {/* SVG inline per Visa con nome */}
                  <div className="h-8 px-3 py-1 bg-white rounded shadow-sm flex items-center justify-center">
                    <svg className="h-5 w-8" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M290 200H350L330 300H270L290 200Z" fill="#00579F" />
                      <path d="M400 200H460L480 300H420L400 200Z" fill="#00579F" />
                      <path d="M330 300H390L420 200H480L450 300H510L470 350H330L350 320L330 300Z" fill="#00579F" />
                      <path d="M290 200H350L260 300H200L290 200Z" fill="#00579F" />
                    </svg>
                    <span className="ml-1 text-xs font-medium text-gray-700">Visa</span>
                  </div>

                  {/* SVG inline per Mastercard con nome */}
                  <div className="h-8 px-3 py-1 bg-white rounded shadow-sm flex items-center justify-center">
                    <svg className="h-5 w-8" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="280" cy="250" r="100" fill="#EB001B" />
                      <circle cx="380" cy="250" r="100" fill="#F79E1B" />
                      <path d="M330 250C330 232.4 335.4 215.6 345.4 202.2C355.4 188.8 369.3 179.4 385.1 175.4C401 171.4 417.9 173 432.8 179.9C447.7 186.8 459.9 198.4 467.4 213C460.3 227.2 448.9 238.9 434.9 246.2C420.9 253.4 404.9 256 389.3 253.5C373.7 251 359.1 243.5 347.7 231.9C336.3 220.4 328.7 205.4 326.2 189.2" fill="#FF5F00" />
                    </svg>
                    <span className="ml-1 text-xs font-medium text-gray-700">Mastercard</span>
                  </div>

                  {/* SVG inline per American Express con nome */}
                  <div className="h-8 px-3 py-1 bg-white rounded shadow-sm flex items-center justify-center">
                    <svg className="h-5 w-8" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="200" y="150" width="380" height="200" rx="10" fill="#006FCF" />
                      <path d="M240 200H350L320 250L350 300H240V200Z" fill="white" />
                      <path d="M380 200H540V300H380L410 250L380 200Z" fill="white" />
                      <path d="M340 225H290V275H340V225Z" fill="#006FCF" />
                      <path d="M460 225H410V275H460V225Z" fill="#006FCF" />
                    </svg>
                    <span className="ml-1 text-xs font-medium text-gray-700">AmEx</span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleConfirmBooking}
              disabled={(!hasExistingAppointments && !consentoInformato) || isProcessing}
              className={`w-full py-2 px-4 rounded transition-colors ${(hasExistingAppointments || consentoInformato) && !isProcessing
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
            >
              {isProcessing
                ? "Elaborazione in corso..."
                : couponSuccess
                  ? "Conferma Prenotazione"
                  : "Conferma e Procedi al Pagamento"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientBookingSummaryPage;